import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Target, Sparkles, Check, AlertTriangle } from "lucide-react";

const INDUSTRIES = ["SaaS", "Fintech", "Healthtech", "E-commerce", "Logistics", "AI / ML", "Cybersecurity", "EdTech", "PropTech", "ClimateTech", "Nonprofit", "Founder", "Arts & Culture", "Hospitality", "Film", "Photography", "Science", "Education"];
const SIZES = ["1-10", "11-50", "51-200", "201-500", "501-1K", "1K-5K", "5K+"];

export default function ICPSetup({ onboarding = false }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ industry: "", role_title: "", company_size: "", geography: "", keywords: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // one-shot mount fetch of existing ICP
  useEffect(() => {
    api.get("/icp").then(({ data }) => {
      if (data) setForm({
        industry: data.industry || "",
        role_title: data.role_title || "",
        company_size: data.company_size || "",
        geography: data.geography || "",
        keywords: data.keywords || "",
      });
      setLoading(false);
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/icp", form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (onboarding) nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save your ICP. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-12 text-zinc-500">Loading...</div>;

  return (
    <div className="p-8 lg:p-12 max-w-3xl">
      <div className="mb-8">
        <div className="label-eyebrow mb-2 flex items-center gap-2">
          <Target className="w-3.5 h-3.5" /> Targeting
        </div>
        <h1 className="font-display text-4xl font-medium text-white tracking-tight">
          {onboarding ? "Set your ICP" : "Ideal Customer Profile"}
        </h1>
        <p className="text-zinc-400 mt-2">
          Describe who you'd like to reach. Real sourcing (ProPublica) is available today for Nonprofit ICPs — other industries use demo mode.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="icp-form">
        <div className="glass-card p-6 space-y-5">
          <div>
            <label className="label-eyebrow block mb-2">Industry</label>
            <div className="flex flex-wrap gap-2">
              {INDUSTRIES.map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setForm({ ...form, industry: i })}
                  data-testid={`industry-${i}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    form.industry === i
                      ? "bg-gold text-black border-gold"
                      : "bg-white/5 text-zinc-300 border-white/10 hover:border-gold/40"
                  }`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Target role / title</label>
            <input
              required
              value={form.role_title}
              onChange={(e) => setForm({ ...form, role_title: e.target.value })}
              className="input-glass"
              placeholder="VP of Sales, Head of Growth, Founder..."
              data-testid="icp-role"
            />
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Company size</label>
            <div className="flex flex-wrap gap-2">
              {SIZES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setForm({ ...form, company_size: s })}
                  data-testid={`size-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    form.company_size === s
                      ? "bg-gold text-black border-gold"
                      : "bg-white/5 text-zinc-300 border-white/10 hover:border-gold/40"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Geography</label>
            <input
              value={form.geography}
              onChange={(e) => setForm({ ...form, geography: e.target.value })}
              className="input-glass"
              placeholder="United States, Western Europe, ANZ..."
              data-testid="icp-geo"
            />
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Signal keywords (optional)</label>
            <textarea
              rows={3}
              value={form.keywords}
              onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              className="input-glass resize-none"
              placeholder="Just raised Series A, hiring SDRs, switched from HubSpot..."
              data-testid="icp-keywords"
            />
          </div>
        </div>

        <div className="glass-card p-5 flex items-start gap-3 border-yellow-300/20 bg-yellow-300/[0.03]">
          <Sparkles className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-white mb-1">My Leads Convert preview</div>
            <div className="text-sm text-zinc-400">
              Looking for <span className="text-gold">{form.role_title || "[role]"}</span> at <span className="text-gold">{form.industry || "[industry]"}</span> companies
              {form.company_size && <> sized <span className="text-gold">{form.company_size}</span></>}
              {form.geography && <> in <span className="text-gold">{form.geography}</span></>}.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button type="submit" disabled={busy || !form.industry || !form.role_title || !form.company_size} className="btn-primary" data-testid="icp-save">
            {busy ? "Saving..." : saved ? <><Check className="w-4 h-4" /> ICP saved</> : (onboarding ? "Save ICP →" : "Save ICP")}
          </button>
          {saved && <span className="text-emerald-400 text-sm">Now head to the Leads page and click "Mine real leads" to pull matching nonprofits from ProPublica.</span>}
        </div>
        {error && (
          <div className="glass-card p-3 px-4 text-sm text-rose-300 border border-rose-500/30 flex items-center gap-2" data-testid="icp-error">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}
      </form>
    </div>
  );
}
